<template>
    <div class="flex flex-col gap-4" data-testid="home-mywork-trends-detail">
        <div class="flex flex-col gap-1">
            <p class="body-small text-ink-muted">{{ progress.period_range_label }}</p>
            <p class="body-small text-ink-muted">{{ t("chat.home.workIq.trends.intro") }}</p>
        </div>

        <div class="flex flex-col gap-1">
            <p class="body-semibold text-ink">{{ headline.phrase || t("chat.home.workIq.headlineShifting", { label: headline.label }) }}</p>
            <p v-if="headline.context" class="body-small text-ink-muted">{{ headline.context }}</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div v-for="metric in progress.metrics" :key="metric.metric" class="rounded-lgr border border-border p-3 flex flex-col gap-2">
                <div class="flex items-center justify-between gap-2">
                    <span class="body-small-semibold text-ink">{{ metric.label }}</span>
                    <span class="body-small-semibold" :class="trendClass(metric.trend)">
                        {{ formatValue(metric.recent) }}
                        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- punctuation around a formatted number -->
                        <span v-if="metric.pct_change !== null">({{ formatPct(metric.pct_change) }})</span>
                    </span>
                </div>
                <div class="relative h-20">
                    <canvas :ref="(el) => setCanvasRef(metric.metric, el)" role="img" :aria-label="chartLabel(metric)"></canvas>
                </div>
                <p class="body-small text-ink-muted">{{ metric.observation }}</p>
                <table class="sr-only">
                    <caption>
                        {{
                            t("chat.home.workIq.trends.tableCaption", { metric: metric.label })
                        }}
                    </caption>
                    <thead>
                        <tr>
                            <th scope="col">{{ t("chat.home.workIq.trends.weekColumn") }}</th>
                            <th scope="col">{{ metric.label }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="point in metric.series" :key="point.period">
                            <th scope="row">{{ t("chat.home.workIq.trends.weekOf", { week: formatIsoWeekStart(point.period) }) }}</th>
                            <td>{{ formatValue(point.value) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { formatIsoWeekStart } from "~vue/pages/developmentJourneys/weekNavigation";
import type { MyWorkProgressData, MyWorkProgressMetric } from "~vue/types/chat_home";
import { formatNumber } from "~vue/utils/formatNumber";
import { CategoryScale, Chart, LinearScale, LineController, LineElement, PointElement } from "chart.js";
import { onMounted, onUnmounted, watch } from "vue";
import { useI18n } from "vue-i18n";

import { cssColor } from "./workiqTrendColors";

const props = defineProps<{
    progress: MyWorkProgressData;
    headline: MyWorkProgressData["headline"];
}>();

const { t } = useI18n();

const canvases = new Map<string, HTMLCanvasElement>();
const charts = new Map<string, Chart>();

function setCanvasRef(metricKey: string, el: unknown) {
    if (el instanceof HTMLCanvasElement) {
        canvases.set(metricKey, el);
    }
}

function formatValue(value: number | null): string {
    if (value === null) {
        return "—";
    }
    return formatNumber(value, "up-to-one-decimal");
}

function chartLabel(metric: MyWorkProgressMetric): string {
    const weeks = props.progress.weeks;
    const name = t("chat.home.workIq.trends.chartName", { metric: metric.label, weeks }, weeks);
    return metric.series.length ? t("chat.home.workIq.trends.chartLabelWithSummary", { name, summary: chartSummary(metric) }) : name;
}

function chartSummary(metric: MyWorkProgressMetric): string {
    const values = metric.series.map((point) => point.value);
    const low = Math.min(...values);
    const high = Math.max(...values);
    const count = values.length;
    return low === high
        ? t("chat.home.workIq.trends.chartSummaryUniform", { count, value: formatValue(low) }, count)
        : t("chat.home.workIq.trends.chartSummaryRange", { count, low: formatValue(low), high: formatValue(high) }, count);
}

function formatPct(pct: number): string {
    // Math.round first so -12.5% stays -12% as it always was; Intl alone rounds it away from zero to -13%.
    return formatNumber(Math.round(pct * 100) / 100, "signed-percent");
}

// Kept neutral in the chart line itself; see HomeMyWorkProgressCard for why observed shape and
// coaching verdict stay visually separate. The value text is the one place a verdict is shown.
function trendClass(trend: string): string {
    if (trend === "improving") {
        return "text-chip-status-done-text";
    }
    if (trend === "declining") {
        return "text-chip-status-overdue-text";
    }
    return "text-ink-muted";
}

function renderChart(metric: MyWorkProgressMetric) {
    const canvas = canvases.get(metric.metric);
    if (!canvas) {
        return;
    }
    charts.get(metric.metric)?.destroy();
    if (!metric.series.length) {
        return;
    }

    const color = cssColor("--color-my-work-trend-line", "#0061fe");
    const chart = new Chart(canvas, {
        type: "line",
        data: {
            labels: metric.series.map((point) => point.period),
            datasets: [
                {
                    data: metric.series.map((point) => point.value),
                    borderColor: color,
                    backgroundColor: `${color}1a`,
                    fill: true,
                    tension: 0.3,
                    pointRadius: 2,
                    borderWidth: 2,
                },
            ],
        },
        options: {
            animation: false,
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                x: { ticks: { display: false }, grid: { display: false } },
                y: { ticks: { color: cssColor("--color-ink-muted", "#616161"), font: { family: "Inter, sans-serif", size: 10 }, maxTicksLimit: 3 }, grid: { display: false } },
            },
            plugins: {
                legend: { display: false },
                tooltip: { enabled: true },
            },
        },
    });
    charts.set(metric.metric, chart);
}

function renderAll() {
    for (const metric of props.progress.metrics) {
        renderChart(metric);
    }
}

watch(() => props.progress, renderAll, { deep: true });

onMounted(() => {
    Chart.register(LineController, LinearScale, CategoryScale, PointElement, LineElement);
    renderAll();
});

onUnmounted(() => {
    for (const chart of charts.values()) {
        chart.destroy();
    }
    charts.clear();
});
</script>
